import { BLACK_ONLY_EQUIPMENT_KIND, EQUIPMENTS_ENDPOINT } from "../../../../../consts";
import { useCRUD } from "../../../../../api/crud";
import { useParams } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import { Equipment } from "../../../../../types/equipments";
import { AddressDisplay } from "../../../../../components/ui/addressDisplay/addressDisplay.component";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../../../../components/ui/breadcrumps/breadcrumbsContext";
import PrintIcon from "@mui/icons-material/Print";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { Inventory2Outlined, Person4 } from "@mui/icons-material";
import { useHasPermission, useTenantName } from "../../../../../contexts/auth";
import EntityReferenceLink from "../../../../../components/ui/entityReference/entityReferenceLink.component";
import {
  DetailGrid,
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
  DetailValue,
} from "../../../../../components/ui/detailView";
import { EquipmentConfigurationChips } from "../../../../../components/ui/equipmentConfigurationChips/equipmentConfigurationChips.component";
import { StatusChip } from "../../../../../components/ui/statusChip/statusChip.component";
import { BooleanIcon } from "../../../../../components/ui/booleanIcon/booleanIcon.component";
import { formatDate, formatDateTimeInline } from "../../../../../utils";

type EquipmentBasicInfoProps = {
  previewId?: string | number;
};

export const EquipmentBasicInfo = ({ previewId }: EquipmentBasicInfoProps) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(EQUIPMENTS_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const canViewClients = useHasPermission("settlement.view_client");
  const canViewContracts = useHasPermission("settlement.view_contract");
  const canViewProfitability = useHasPermission("settlement.can_view_profitability");
  // An empty page cost - or one of 0, which counts as none - is filled in by
  // profitability with the cost calculated from the parts used in the
  // machine's model, or - with no such part - the workspace default set on
  // the Profitability page.
  const pageCost = (cost?: string | null, calculatedCost?: string | null) => {
    if (cost != null && Number(cost) !== 0) return cost;
    const fallbackSx = { color: "text.secondary", fontStyle: "italic" };
    if (calculatedCost != null) {
      return (
        <span>
          {calculatedCost}{" "}
          <Typography variant="subtitle1" component="span" sx={fallbackSx}>
            {t("(calculated from parts)")}
          </Typography>
        </span>
      );
    }
    return (
      <Typography variant="subtitle1" component="span" sx={fallbackSx}>
        {t("Workspace default")}
      </Typography>
    );
  };
  // A machine with no installation address is not out at a client's site - it
  // is back with us, so the workspace's own name is where it currently is.
  const tenantName = useTenantName();
  const [equipment, setEquipment] = useState<Equipment | null>(null);
  const entityId = previewId ?? id;

  useEffect(() => {
    const fetchEquipment = async () => {
      if (entityId) {
        const response = await get(entityId);
        setOverride(`/equipment-section/equipments/${entityId}`, response.data ? response.data.full_name : "");
        setEquipment(response.data);
      }
    };

    const handleEquipmentUpdated = () => {
      fetchEquipment();
    };

    window.addEventListener("equipmentUpdated", handleEquipmentUpdated);
    fetchEquipment();
    return () => {
      window.removeEventListener("equipmentUpdated", handleEquipmentUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  if (!equipment) return <DetailPage loading />;
  // Black-only equipment has no color page cost - see BLACK_ONLY_EQUIPMENT_KIND.
  const isBlackOnly = equipment.kind === BLACK_ONLY_EQUIPMENT_KIND;
  const installationAddress = equipment.installation_address;
  const currentContracts = equipment.current_contracts ?? [];

  return (
    <DetailPage>
      <DetailHeader
        icon={<PrintOutlinedIcon />}
        title={equipment.full_name}
        subtitle={equipment.serial && `${t("Serial Number")}: ${equipment.serial}`}
        chips={
          <>
            <StatusChip icon={<PrintIcon />} label={equipment.kind_display} color="primary" />
            <StatusChip icon={<Person4 />} label={equipment.ownership_display} color="secondary" />
            {equipment.is_archived && (
              <StatusChip
                icon={<Inventory2Outlined />}
                label={t("Archived")}
                title={t("Archived equipment does not count towards the subscription plan's device limit from the first month it spends archived, and takes no new jobs or counter readings.")}
              />
            )}
          </>
        }
      />

      <DetailGrid>
        <DetailSection title={t("Basic Information")}>
          <DetailRow label={t("Client Name")}>
            <EntityReferenceLink
              entityType="client"
              entityId={equipment.client_id}
              label={equipment.client_name}
              canPreview={canViewClients}
              stopPropagation={false}
            />
          </DetailRow>
          {currentContracts.length === 0 && <DetailRow label={t("Current contract")} />}
          {currentContracts.map((contract) => (
            <Fragment key={contract.contract_term_id}>
              <DetailRow label={t("Current contract")}>
                <EntityReferenceLink
                  entityType="contract"
                  entityId={contract.contract_id}
                  label={[contract.number, contract.internal_name].filter(Boolean).join(" - ")}
                  canPreview={canViewContracts}
                  stopPropagation={false}
                />
              </DetailRow>
              <DetailRow label={t("Added to contract")} value={formatDate(contract.added_at)} />
            </Fragment>
          ))}
          <DetailRow
            label={t("Producer and model")}
            value={[equipment.producer, equipment.model].filter(Boolean).join(" ")}
          />
          <DetailRow label={t("Serial Number")} value={equipment.serial} />
          <DetailRow label={t("Kind")} value={equipment.kind_display} />
          <DetailRow label={t("Ownership")} value={equipment.ownership_display} />
          <DetailRow label={t("Is installed")} value={<BooleanIcon value={equipment.is_installed} />} />
          {equipment.is_archived && (
            <DetailRow label={t("Archived at")} value={formatDateTimeInline(equipment.archived_at)} />
          )}
          {/* Only shown once the machine has actually been sold - on rented
              equipment there is no guarantee of ours to report. */}
          {equipment.sold_at && (
            <>
              <DetailRow label={t("Sold at")} value={equipment.sold_at} />
              <DetailRow label={t("Warranty until")}>
                {equipment.warranty_until ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DetailValue>{equipment.warranty_until}</DetailValue>
                    <StatusChip
                      color={equipment.is_under_warranty ? "success" : "default"}
                      variant="outlined"
                      label={
                        equipment.is_under_warranty ? t("Under warranty") : t("Warranty expired")
                      }
                    />
                  </Box>
                ) : null}
              </DetailRow>
            </>
          )}
          {equipment.notes && <DetailRow label={t("Notes")} value={equipment.notes} multiline />}
        </DetailSection>

        <DetailSection title={t("Configuration")} padded>
          <EquipmentConfigurationChips attributeValues={equipment.attribute_values} />
        </DetailSection>

        {canViewProfitability && (
          <DetailSection title={t("Page costs")}>
            <DetailRow
              label={t("Black page cost")}
              value={pageCost(equipment.bp_usage_cost, equipment.parts_usage_costs?.bp_usage_cost)}
            />
            {!isBlackOnly && (
              <DetailRow
                label={t("Color page cost")}
                value={pageCost(equipment.cp_usage_cost, equipment.parts_usage_costs?.cp_usage_cost)}
              />
            )}
            <DetailRow
              label={t("Scanned page cost")}
              value={pageCost(equipment.sp_usage_cost, equipment.parts_usage_costs?.sp_usage_cost)}
            />
          </DetailSection>
        )}

        <AddressDisplay
          address={installationAddress}
          title={`${t("Installation Address")} & ${t("Opening Hours")}`}
          withDetails
          withContact
          collapsible
          emptyContent={
            <Typography variant="subtitle1" sx={{ fontStyle: "italic", color: "text.secondary" }}>
              {tenantName || "—"}
            </Typography>
          }
        />

      </DetailGrid>

      <DetailMetadata createdAt={equipment.created_at} updatedAt={equipment.updated_at} />
    </DetailPage>
  );
};
